import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@packages/utils';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import PageWrapper from 'components/Base/PageWrapper';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import useTable from 'components/Base/Table/hooks/useTable';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import CardContent from 'components/Base/Layout/Content/CardContent';
import { EWaitListColumnKey, TWaitListUI } from 'data/types/registrants.types';
import {
  useGetWaitListCitiesQuery,
  useGetWaitListLocationsQuery,
  useGetWaitListQuery,
  useExportWaitlistMutation,
} from 'redux/apiSlice/registrants.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import useNavigateWithBackState from 'hooks/useNavigateWithBackState';
import { EKitManagementParentColumnKey } from 'data/types/kitManagement.types';
import { ETagType } from 'data/types/tags.types';
import ActiveFilterToggle from 'components/Shared/ActiveFilterToggle';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import useGetColumnsData from './WaitList.data';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EWaitListColumnKey.CREATION_DATE]: 'registrationDate',
  [EWaitListColumnKey.LOCATION]: 'location',
  [EWaitListColumnKey.CITIES]: 'city',
  [EWaitListColumnKey.TYPE]: 'workshopType',
  [EWaitListColumnKey.AGE_GROUP]: 'ageGroup',
  [EWaitListColumnKey.FIRST_NAME]: 'firstName',
  [EWaitListColumnKey.LAST_NAME]: 'lastName',
  email: 'email',
  phone: 'phone',
  [EWaitListColumnKey.TAGS]: 'tags',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EWaitListColumnKey.CREATION_DATE]: 'CREATION_DATE',
  [EWaitListColumnKey.FIRST_NAME]: 'FIRST_NAME',
  [EWaitListColumnKey.LAST_NAME]: 'LAST_NAME',
};

const WaitList = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.waitlist.list',
  });

  const { t: tWaitList } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.workshops.details.workshop-wait-list',
  });

  const [showActiveOnly, setShowActiveOnly] = useState(() => {
    const storedValue = localStorage.getItem('waitlist_show_active_only');
    if (storedValue === null) {
      localStorage.setItem('waitlist_show_active_only', JSON.stringify(true));
      return true;
    }
    return JSON.parse(storedValue);
  });

  const { defaultColumns } = useGetColumnsData();
  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TWaitListUI>({
    storagePath: ETableStoragePaths.SUBSCRIBERS_WAIT_LIST,
    defaultColumns,
  });

  const { data, isFetching, isLoading, error } = useGetWaitListQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
    activeOnly: showActiveOnly,
  });

  const { data: citiesFilters, isSuccess: citiesFiltersSuccess } =
    useGetWaitListCitiesQuery();
  const { data: locationFilters, isSuccess: locationFiltersSuccess } =
    useGetWaitListLocationsQuery();

  const { data: tagsFilters, isSuccess: tagsFiltersSuccess } =
    useGetTagsListQuery(ETagType.REGISTRANT);

  useEffect(() => {
    if (citiesFiltersSuccess) {
      setColumns(p => {
        return p.map(item => {
          if (item.key === EWaitListColumnKey.CITIES) {
            item.filters = citiesFilters;
            item.filterSearch = true;
          }
          return item;
        });
      });
    }
  }, [citiesFiltersSuccess, citiesFilters]);

  useEffect(() => {
    if (tagsFiltersSuccess) {
      setColumns(p => {
        return p.map(item => {
          if (item.key === EKitManagementParentColumnKey.TAGS) {
            item.filters = tagsFilters;
            item.filterSearch = true;
          }
          return item;
        });
      });
    }
  }, [tagsFiltersSuccess, tagsFilters]);

  useEffect(() => {
    if (locationFiltersSuccess) {
      setColumns(p => {
        return p.map(item => {
          if (item.key === EWaitListColumnKey.LOCATION) {
            item.filters = locationFilters;
            item.filterSearch = true;
          }
          return item;
        });
      });
    }
  }, [locationFiltersSuccess, locationFilters]);

  const { goWorkshopDetails } = useNavigateWithBackState();

  const navigateToWorkshopDetails = (record: TWaitListUI) => {
    return {
      onClick: () => goWorkshopDetails(record.workshopId, t('TITLE')),
    };
  };

  const handleActiveToggle = (checked: boolean) => {
    setShowActiveOnly(checked);
    localStorage.setItem('waitlist_show_active_only', JSON.stringify(checked));
  };
  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;

  const [exportWaitlist, { isLoading: isExporting }] =
    useExportWaitlistMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.SUBSCRIBERS_WAIT_LIST
    ] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = selectedFilters.email[0] as string;
      }
      if (selectedFilters?.cities) {
        filters.cities = selectedFilters.cities;
      }
      if (selectedFilters?.ageGroups) {
        filters.ageGroups = selectedFilters.ageGroups;
      }
      if (selectedFilters?.workshopTypes) {
        filters.workshopTypes = selectedFilters.workshopTypes;
      }
      if (selectedFilters?.locationIds) {
        filters.locationIds = selectedFilters.locationIds;
      }
      if (selectedFilters?.tagIds) {
        filters.tagIds = selectedFilters.tagIds;
      }
      if (showActiveOnly) {
        filters.activeOnly = true;
      }
      return Object.keys(filters).length > 0 ? filters : undefined;
    },
    [selectedFilters, showActiveOnly],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportWaitlist({
        columns: exportColumns,
        ...(buildExportFilters() && { filters: buildExportFilters() }),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportWaitlist,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportWaitlist({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportWaitlist,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const tableHeaderActions = useMemo(
    () => [
      <ActiveFilterToggle
        key="active-filter"
        checked={showActiveOnly}
        onChange={handleActiveToggle}
        label={tWaitList('actions.show-active-only.LABEL')}
        tooltipText={tWaitList('actions.show-active-only.TOOLTIP')}
        storageKey="waitlist_show_active_only"
        defaultValue
        data-cy="waitlist-show-active-only-toggle"
      />,
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />,
    ],
    [showActiveOnly, handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <PageWrapper
      title={t('TITLE')}
      isLoading={isLoading}
      error={error as TRtkErrorType}
      emptyWrapper={{
        isEmpty,
        alt: 'Sand Timer',
        icon: EIconNames.SAND_TIMER_FILLED,
        emptyText: t('empty-view.EMPTY_TEXT'),
      }}
    >
      <CardContent>
        <Table<TWaitListUI>
          headerActions={tableHeaderActions}
          onFiltersReset={onFiltersReset}
          rowKey={record => record.registrantId}
          hasFilters={hasFilters}
          currentPage={page}
          columns={columns}
          dataSource={data?.content}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements}
          onChange={handleTableChange}
          storagePath={ETableStoragePaths.SUBSCRIBERS_WAIT_LIST}
          onRow={navigateToWorkshopDetails}
          rowClassName="pointer"
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </CardContent>
    </PageWrapper>
  );
};

export default WaitList;
